import { Flash16 } from '@carbon/icons-react';
import Analytics16 from '@carbon/icons-react/es/analytics/16';
import EventsAlt16 from '@carbon/icons-react/es/events--alt/16';
import Home16 from '@carbon/icons-react/es/home/16';
import ListChecked16 from '@carbon/icons-react/es/list--checked/16';
import Meter16 from '@carbon/icons-react/es/meter/16';
import Notification16 from '@carbon/icons-react/es/notification/16';
import Settings16 from '@carbon/icons-react/es/settings/16';
import Task16 from '@carbon/icons-react/es/task/16';
import Template16 from '@carbon/icons-react/es/template/16';
import Timer16 from '@carbon/icons-react/es/timer/16';
import { Enterprise } from '@carbon/icons-react/next';
import React from 'react';
import styled from 'styled-components';
import { TimesheetAccessState } from 'api/account';
import { UserType } from 'api/models';
import { ReactComponent as AllSprints } from 'assets/svgs/all-sprints.svg';
import { useNotificationsDrawer } from 'components/organisms/notifications/drawer/hash-based-notifications';
import { RoutePaths } from 'constant';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useAccountDataQuery } from 'queries/account';
import { CommandGroup, CommandItem, CommandItemType } from '../types';

export const useGotoEntryForCmdk = (): CommandGroup => {
  const translate = useTranslate();
  const currentUser = useUser();
  const user = currentUser?.user;

  const [, show] = useNotificationsDrawer();
  const { data: accountData } = useAccountDataQuery();

  const { hasAccessToResource } = useWithAccess();

  const rbacTimeSheetAccess = hasAccessToResource({
    actionScope: ['PRIVATE'],
    rule: 'TIMESHEET:READ',
  });

  const hasAccessToAccounts = hasAccessToResource({
    rule: 'CUSTOMER_COMPANY:READ',
  });
  const hasAccessToAutomations = hasAccessToResource({
    rule: 'SETTINGS_AUTOMATION:READ',
  });
  const [isSprintPlanningFeatureEnabled] = useAccountFeature(
    AccountFeature.SprintPlanning,
  );
  const SprintIcon = styled(AllSprints)`
    fill: var(--white-100);
  `;

  const isSprintPlanningShown =
    isSprintPlanningFeatureEnabled || (accountData?.doesSprintsExist ?? false);

  const hasAccessToTimesheetMenu =
    rbacTimeSheetAccess &&
    accountData?.timeSheetAccess !== TimesheetAccessState.Disabled;

  const isCurrentUserVendor = user.userType === UserType.Native;

  return {
    groupId: 1000,
    tabbable: false,
    filterable: false,
    label: translate('go_to'),
    name: 'go_to',
    getCommandItems: async (filterText) => {
      const actions: CommandItem[] = [
        {
          label: translate('home.title'),
          icon: () => <Home16 />,
          meta: {
            type: CommandItemType.Navigate,
            link: RoutePaths.home,
          },
          name: 'go_to_home',
        },
        {
          label: translate('notifications'),
          icon: () => <Notification16 />,
          meta:
            user.userType === UserType.External
              ? {
                  type: CommandItemType.Navigate,
                  link: RoutePaths.notifications,
                }
              : {
                  type: CommandItemType.Callback,
                  callback: show,
                },

          name: 'go_to_home',
        },
        ...(isCurrentUserVendor
          ? ([
              {
                label: translate('dashboard'),
                icon: () => <Meter16 />,
                meta: {
                  type: CommandItemType.Navigate,
                  link: RoutePaths.dashboard,
                },
                name: 'go_to_dashboards',
              },
              {
                label: translate('reports'),
                icon: () => <Analytics16 />,
                meta: {
                  type: CommandItemType.Navigate,
                  link: RoutePaths.reports,
                },
                name: 'go_to_reports',
              },
              ...(hasAccessToAccounts
                ? ([
                    {
                      label: translate('menu.label.accounts'),
                      icon: () => <Enterprise />,
                      meta: {
                        type: CommandItemType.Navigate,
                        link: RoutePaths.customerPorfolio,
                      },
                      name: 'go_to_accounts',
                    },
                  ] as CommandItem[])
                : ([] as CommandItem[])),
              {
                icon: () => <Task16 />,
                label: translate('breadcrumbs.projects'),
                meta: {
                  type: CommandItemType.Navigate,
                  link: RoutePaths.projects,
                },
                name: 'go_to_projects',
              },
              {
                label: translate('menu.label.tasks'),
                icon: () => <ListChecked16 />,
                meta: {
                  type: CommandItemType.Navigate,
                  link: RoutePaths.tasks,
                },
                name: 'go_to_all_tasks',
              },
              ...(isSprintPlanningShown
                ? ([
                    {
                      label: translate('menu.label.sprints'),
                      icon: () => <SprintIcon />,
                      meta: {
                        type: CommandItemType.Navigate,
                        link: RoutePaths.sprintPlanning,
                      },
                      name: 'go_to_sprints',
                    },
                  ] as CommandItem[])
                : ([] as CommandItem[])),
              {
                label: translate('menu.label.resource_management'),
                icon: () => <EventsAlt16 />,
                meta: {
                  type: CommandItemType.Navigate,
                  link: RoutePaths.resourceManagement,
                },
                name: 'go_to_rm',
              },
            ] as CommandItem[])
          : ([] as CommandItem[])),

        ...(hasAccessToTimesheetMenu
          ? ([
              {
                label: translate('menu.label.timesheets'),
                icon: () => <Timer16 />,
                meta: {
                  type: CommandItemType.Navigate,
                  link: RoutePaths.timesheets,
                },
                name: 'go_to_timesheets',
              },
            ] as CommandItem[])
          : ([] as CommandItem[])),
        ...(isCurrentUserVendor
          ? ([
              {
                label: translate('menu.label.templates'),
                icon: () => <Template16 />,
                meta: {
                  type: CommandItemType.Navigate,
                  link: RoutePaths.templates,
                },
                name: 'go_to_templates',
              },
            ] as CommandItem[])
          : ([] as CommandItem[])),
        {
          label: translate('menu.label.settings'),
          icon: () => <Settings16 />,
          meta: {
            type: CommandItemType.Navigate,
            link: RoutePaths.settings,
          },
          name: 'go_to_settings',
        },
        ...((hasAccessToAutomations
          ? [
              {
                label: translate('automations'),
                icon: () => <Flash16 />,
                meta: {
                  type: CommandItemType.Navigate,
                  link: RoutePaths.globalAutomations,
                },
                name: 'automations',
              },
            ]
          : []) as CommandItem[]),
      ];

      return filterText !== ''
        ? actions.filter((e) =>
            e.label
              .replaceAll(' ', '')
              .toLowerCase()
              .includes(filterText.replaceAll(' ', '').toLowerCase()),
          )
        : actions;
    },
  };
};
